import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Lock, LogOut, RefreshCw, Inbox, Loader2 } from "lucide-react";
import { SERVICE_OPTIONS } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ig_admin_token";

const serviceLabel = (v) => SERVICE_OPTIONS.find((o) => o.value === v)?.label || v;

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return iso;
    }
};

const STATUS_META = {
    new: { label: "New", cls: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10" },
    "in-progress": { label: "In Progress", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
    done: { label: "Done", cls: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
};
const STATUS_ORDER = ["new", "in-progress", "done"];

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [enquiries, setEnquiries] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [filter, setFilter] = useState("all");

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setEnquiries(null);
    }, []);

    const loadEnquiries = useCallback(
        async (t) => {
            setLoadError("");
            try {
                const { data } = await axios.get(`${API}/enquiries`, {
                    headers: { Authorization: `Bearer ${t}` },
                });
                setEnquiries(data.enquiries);
            } catch (e) {
                if (e?.response?.status === 401) {
                    logout();
                } else {
                    setLoadError("Could not load enquiries. Please try again.");
                }
            }
        },
        [logout],
    );

    const updateStatus = async (id, status) => {
        try {
            await axios.patch(
                `${API}/enquiries/${id}/status`,
                { status },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setEnquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
        } catch (e) {
            if (e?.response?.status === 401) logout();
            else setLoadError("Could not update the status. Please try again.");
        }
    };

    useEffect(() => {
        if (token) loadEnquiries(token);
    }, [token, loadEnquiries]);

    const onLogin = async (e) => {
        e.preventDefault();
        setLoginError("");
        setLoggingIn(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            localStorage.setItem(TOKEN_KEY, data.token);
            setToken(data.token);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setLoginError(typeof detail === "string" ? detail : "Login failed. Please try again.");
        } finally {
            setLoggingIn(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Admin | Innovated Global IT Solutions</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <section className="py-20 lg:py-24" style={{ minHeight: "60vh" }} data-testid="admin-page">
                <div className="container-x max-w-5xl">
                    {!token ? (
                        <div className="mx-auto max-w-md">
                            <form onSubmit={onLogin} className="glass rounded-3xl p-8 sm:p-10" data-testid="admin-login-form">
                                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: "rgba(0,102,255,0.14)", color: "#00F0FF" }}>
                                    <Lock size={22} aria-hidden="true" />
                                </span>
                                <h1 className="mt-5 text-2xl font-semibold">Admin sign in</h1>
                                <p className="mt-2 text-sm text-slate-400">Private area for the Innovated Global team.</p>

                                <label htmlFor="admin-email" className="mt-7 mb-2 block text-sm font-medium text-slate-300">Email</label>
                                <input
                                    id="admin-email"
                                    type="email"
                                    autoComplete="username"
                                    className="field"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="admin-email-input"
                                />

                                <label htmlFor="admin-password" className="mt-4 mb-2 block text-sm font-medium text-slate-300">Password</label>
                                <input
                                    id="admin-password"
                                    type="password"
                                    autoComplete="current-password"
                                    className="field"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="admin-password-input"
                                />

                                {loginError && (
                                    <p className="mt-4 text-sm text-rose-400" role="alert" data-testid="admin-login-error">{loginError}</p>
                                )}

                                <button type="submit" className="btn-primary mt-7 w-full" disabled={loggingIn} data-testid="admin-login-button">
                                    {loggingIn ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
                                    {loggingIn ? "Signing in…" : "Sign In"}
                                </button>
                            </form>
                        </div>
                    ) : (
                        <div data-testid="admin-dashboard">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <p className="eyebrow text-cyan-300">Private dashboard</p>
                                    <h1 className="mt-2 text-2xl font-semibold sm:text-3xl">Enquiries</h1>
                                </div>
                                <div className="flex gap-3">
                                    <button type="button" className="btn-ghost !px-5 !py-2.5" onClick={() => loadEnquiries(token)} data-testid="admin-refresh-button">
                                        <RefreshCw size={15} aria-hidden="true" />
                                        Refresh
                                    </button>
                                    <button type="button" className="btn-ghost !px-5 !py-2.5" onClick={logout} data-testid="admin-logout-button">
                                        <LogOut size={15} aria-hidden="true" />
                                        Sign out
                                    </button>
                                </div>
                            </div>

                            {loadError && <p className="mt-6 text-sm text-rose-400" role="alert" data-testid="admin-load-error">{loadError}</p>}

                            {enquiries === null ? (
                                <div className="mt-14 flex justify-center" data-testid="admin-loading">
                                    <Loader2 size={28} className="animate-spin text-slate-500" aria-hidden="true" />
                                </div>
                            ) : enquiries.length === 0 ? (
                                <div className="glass mt-10 rounded-3xl p-12 text-center" data-testid="admin-empty">
                                    <Inbox size={36} className="mx-auto text-slate-500" aria-hidden="true" />
                                    <p className="mt-4 text-sm text-slate-400">No enquiries yet. New contact form submissions will appear here.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-filter-chips">
                                        {["all", ...STATUS_ORDER].map((s) => {
                                            const count = s === "all" ? enquiries.length : enquiries.filter((q) => (q.status || "new") === s).length;
                                            const active = filter === s;
                                            return (
                                                <button
                                                    key={s}
                                                    type="button"
                                                    onClick={() => setFilter(s)}
                                                    data-testid={`filter-chip-${s}`}
                                                    className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-all duration-200 ${
                                                        active
                                                            ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-300"
                                                            : "border-white/10 text-slate-400 hover:border-white/25 hover:text-white"
                                                    }`}
                                                >
                                                    {s === "all" ? "All" : STATUS_META[s].label} · {count}
                                                </button>
                                            );
                                        })}
                                    </div>
                                <div className="mt-6 space-y-4" data-testid="admin-enquiry-list">
                                    {enquiries.filter((q) => filter === "all" || (q.status || "new") === filter).map((q) => (
                                        <article key={q.id} className="glass rounded-2xl p-6" data-testid={`admin-enquiry-${q.id}`}>
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div>
                                                    <h2 className="text-base font-semibold">{q.name}</h2>
                                                    <p className="mt-0.5 text-sm text-slate-400">
                                                        <a href={`mailto:${q.work_email}`} className="hover:text-cyan-300">{q.work_email}</a>
                                                        {q.company ? ` · ${q.company}` : ""}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col items-end gap-1.5 text-right">
                                                    <span
                                                        className={`inline-block rounded-full border px-3 py-1 text-xs font-medium ${STATUS_META[q.status || "new"].cls}`}
                                                        data-testid={`status-badge-${q.id}`}
                                                    >
                                                        {STATUS_META[q.status || "new"].label}
                                                    </span>
                                                    <span className="inline-block rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">
                                                        {serviceLabel(q.service_of_interest)}
                                                    </span>
                                                    <p className="text-xs text-slate-500">{formatDate(q.created_at)}</p>
                                                </div>
                                            </div>
                                            <p className="mt-4 text-sm leading-relaxed text-slate-300">{q.message}</p>
                                            <p className="mt-3 text-xs text-slate-600" style={{ fontFamily: "Fira Code, monospace" }}>
                                                Ref: {q.id}
                                            </p>
                                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                                <span className="text-xs text-slate-500">Mark as:</span>
                                                {STATUS_ORDER.map((s) => {
                                                    const current = (q.status || "new") === s;
                                                    return (
                                                        <button
                                                            key={s}
                                                            type="button"
                                                            disabled={current}
                                                            onClick={() => updateStatus(q.id, s)}
                                                            data-testid={`status-btn-${s}-${q.id}`}
                                                            className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 ${
                                                                current
                                                                    ? `${STATUS_META[s].cls} cursor-default`
                                                                    : "border-white/10 text-slate-400 hover:border-white/25 hover:text-white"
                                                            }`}
                                                        >
                                                            {STATUS_META[s].label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </article>
                                    ))}
                                </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
